/**
 * New Invoice Page - T098
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { InvoiceForm } from '../components/invoices/InvoiceForm';
import { useCreateInvoiceDraft, useMarkInvoiceSent, type Invoice } from '../hooks/useInvoices';
import { useToastHelpers } from '../components/ui/Toast';
import { EmptyState, PageHeader } from '../components/ui';
import type { CreateInvoiceInput } from '@armard/shared';
import type { InvoiceItemDraft } from '../components/invoices/InvoiceItemForm';
import { useState } from 'react';
import { z } from 'zod';
import { useCustomer } from '../hooks/useCustomers';
import { markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';
import { LoadingSpinner } from '../components/ui';

export const Route = createFileRoute('/invoices_/new')({
  validateSearch: z.object({ customerId: z.string().uuid().optional() }),
  component: NewInvoicePage,
});

function NewInvoicePage() {
  const navigate = useNavigate();
  const { customerId } = Route.useSearch();
  const {
    data: preselectedCustomer,
    isLoading: isLoadingCustomer,
    error: customerError,
    refetch: refetchCustomer,
  } = useCustomer(customerId ?? '');
  const toast = useToastHelpers();
  const createInvoice = useCreateInvoiceDraft();
  const markSent = useMarkInvoiceSent();
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (invoiceData: CreateInvoiceInput, items: InvoiceItemDraft[]) => {
    try {
      const invoice = await createInvoice.mutateAsync({ invoice: invoiceData, items });
      markFormSaved();
      toast.success('Rechnung erstellt');
      navigate({ to: '/invoices/$id', params: { id: invoice.id } });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Fehler beim Erstellen'));
    }
  };

  const handleSubmitAndSend = async (
    invoiceData: CreateInvoiceInput,
    items: InvoiceItemDraft[]
  ) => {
    let invoice: Invoice;
    try {
      setIsSending(true);
      invoice = await createInvoice.mutateAsync({ invoice: invoiceData, items });
      markFormSaved();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Fehler beim Erstellen'));
      setIsSending(false);
      return;
    }

    try {
      await markSent.mutateAsync({ id: invoice.id, version: invoice.version });
      toast.success('Rechnung erstellt und als gesendet markiert');
    } catch (error) {
      toast.error(
        `Rechnung gespeichert, aber Senden fehlgeschlagen: ${apiErrorMessage(error, 'Bitte auf der Rechnungsseite erneut versuchen.')}`
      );
    } finally {
      setIsSending(false);
    }
    // The draft already exists, so leave the form even when the send transition failed.
    navigate({ to: '/invoices/$id', params: { id: invoice.id } });
  };

  if (customerId && isLoadingCustomer) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (customerId && (customerError || !preselectedCustomer)) {
    return (
      <ProtectedRoute>
        <AppShell>
          <PageHeader backTo="/invoices" backLabel="Rechnungen" title="Neue Rechnung" />
          <EmptyState
            title="Kunde nicht verfügbar"
            description={
              customerError
                ? apiErrorMessage(
                    customerError,
                    'Der vorausgewählte Kunde konnte nicht geladen werden.'
                  )
                : 'Der vorausgewählte Kunde wurde nicht gefunden oder ist in diesem Mandanten nicht verfügbar.'
            }
            action={{
              label: 'Erneut laden',
              onClick: () => {
                void refetchCustomer();
              },
            }}
          />
        </AppShell>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <AppShell>
        <PageHeader backTo="/invoices" backLabel="Rechnungen" title="Neue Rechnung" />

        <InvoiceForm
          initialCustomer={preselectedCustomer}
          onSubmit={handleSubmit}
          onSubmitAndSend={handleSubmitAndSend}
          isLoading={createInvoice.isPending}
          isSending={isSending}
          onCancel={() => navigate({ to: '/invoices' })}
        />
      </AppShell>
    </ProtectedRoute>
  );
}
